import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'

export const useInvoiceActions = (props = {}) => {
  const { patientId } = props
  const queryClient = useQueryClient()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    mutateAsync: sendPaymentLink,
    isLoading: isSendingPaymentLink,
    isError: isSendPaymentLinkError,
  } = useMutation({
    mutationKey: ['send-payment-link', patientId],
    mutationFn: async ({ invoiceId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/links/new.json`,
        { method: 'GET', headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't create the payment link")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error creating payment link', 'error')
    },
  })

  const {
    mutateAsync: refundInvoice,
    isLoading: isRefundingInvoice,
    isError: isRefundInvoiceError,
  } = useMutation({
    mutationKey: ['refund-invoice', patientId],
    mutationFn: async ({ invoiceId, paymentId, amount, message }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/payments/${paymentId}/refund.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ refund: { amount, message } }),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't refund the invoice")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('We were not able to refund the invoice', 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  const {
    mutateAsync: cancelInvoice,
    isLoading: isCancelingInvoice,
    isError: isCancelInvoiceError,
  } = useMutation({
    mutationKey: ['cancel-invoice', patientId],
    mutationFn: async ({ invoiceId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/cancel.json`,
        {
          method: 'DELETE',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't cancel the invoice")
      }
      return null
    },
    onError: () => {
      onOpenSnackbar('Error cancelling invoice', 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  const {
    mutateAsync: changePaymentMethod,
    isLoading: isChangingPaymentMethod,
    isError: isChangePaymentMethodError,
  } = useMutation({
    mutationKey: ['change-payment-method', patientId],
    mutationFn: async ({ invoiceId, paymentMethodId, isDefaultMethod }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/payment_method.json`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            invoice: {
              payment_method_id: paymentMethodId,
              default: isDefaultMethod ? String(isDefaultMethod) : 'false',
            },
          }),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't change the payment method")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error changing payment method', 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  const {
    mutateAsync: chargeInvoice,
    isLoading: isChargingInvoice,
    isError: isChargingInvoiceError,
  } = useMutation({
    mutationKey: ['charge-invoice', patientId],
    mutationFn: async ({ invoiceId, paymentData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/payments.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            payment_method: paymentData.paymentMethod,
            amount_targeted: paymentData.amountTargeted,
            payment_method_id: paymentData.paymentMethodId,
            gateway: paymentData.gateway,
            additional_info: paymentData.additionalInfo,
          }),
        }
      )
      if (!res.ok) {
        const response = await res.json()
        const error = response.error
        throw new Error(error)
      }
      return res.json()
    },
    onError: error => {
      onOpenSnackbar(error.message, 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  const {
    mutateAsync: scheduleInvoice,
    isLoading: isSchedulingInvoice,
    isError: isScheduleInvoiceError,
  } = useMutation({
    mutationKey: ['schedule-invoice', patientId],
    mutationFn: async ({ invoiceId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/awaiting_process.json`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ invoice_id: invoiceId }),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't schedule the invoice")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error scheduling invoice', 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  const {
    mutateAsync: updateInvoiceDueDate,
    isLoading: isUpdatingInvoiceDueDate,
    isError: isUpdateInvoiceDueDateError,
  } = useMutation({
    mutationKey: ['update-invoice-due-date', patientId],
    mutationFn: async ({ invoiceId, paymentPlanId, dueDate }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/${invoiceId}/due_date.json`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ invoice: { due_date: dueDate } }),
        }
      )
      if (!res.ok) {
        const response = await res.json().catch(() => ({}))
        throw new Error(response.error || "We couldn't update the due date")
      }
      return res.json()
    },
    onError: error => {
      onOpenSnackbar(error.message, 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  const {
    mutateAsync: declinePayment,
    isLoading: isDecliningPayment,
    isError: isDeclinePaymentError,
  } = useMutation({
    mutationKey: ['decline-payment', patientId],
    mutationFn: async ({ invoiceId, paymentId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/billing/invoices/${invoiceId}/payments/${paymentId}/decline.json`,
        {
          method: 'PUT',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't decline the payment")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error declining payment', 'error')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['patient-finance-info', patientId],
      })
    },
  })

  return {
    sendPaymentLink,
    isSendingPaymentLink,
    isSendPaymentLinkError,
    refundInvoice,
    isRefundingInvoice,
    isRefundInvoiceError,
    cancelInvoice,
    isCancelingInvoice,
    isCancelInvoiceError,
    changePaymentMethod,
    isChangingPaymentMethod,
    isChangePaymentMethodError,
    chargeInvoice,
    isChargingInvoice,
    isChargingInvoiceError,
    scheduleInvoice,
    isSchedulingInvoice,
    isScheduleInvoiceError,
    declinePayment,
    isDecliningPayment,
    isDeclinePaymentError,
    updateInvoiceDueDate,
    isUpdatingInvoiceDueDate,
    isUpdateInvoiceDueDateError,
  }
}

export default useInvoiceActions
